import type { LotBuyNowQuote } from '@skinstep/shared';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/apiClient';

type Options = {
  enabled: boolean;
};

export const buyNowQuoteQueryKey = (lotId: string) => ['lot-buy-now-quote', lotId] as const;

// The quote depends only on (lot, viewer) — other bidders' actions don't change
// viewer_bids_count or market_price_cents. We deliberately do NOT key by the
// lot's total bids_count: that would refetch on every broadcast and the few-
// hundred-ms gap before the new quote lands would flicker the Buy Now block
// from visible → empty → visible. Call the invalidator explicitly from the
// viewer's own bid mutation instead.
export function useBuyNowQuote(
  lotId: string,
  { enabled }: Options,
): UseQueryResult<LotBuyNowQuote | null> {
  return useQuery({
    queryKey: buyNowQuoteQueryKey(lotId),
    enabled,
    queryFn: async ({ signal }) => {
      try {
        return await api.get<LotBuyNowQuote>(`/api/lots/${lotId}/buy-now-quote`, { signal });
      } catch (err) {
        // Treat a missing lot as "no quote available" instead of surfacing a hard
        // error to the UI — the surrounding LotDetail already handles 404 on the
        // main lot resource and rendering "buy now unavailable" here would noise
        // the screen for an already-broken lot.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 2_000,
    // Hold previous data through refetches so the Buy Now card doesn't flicker
    // when the viewer places a bid and we invalidate the quote.
    placeholderData: (prev) => prev,
  });
}
